// Shared className tokens for ChatMap node renderers.
//
// Each token is a Tailwind utility string the renderers concatenate
// into their `className` props. The point isn't DRY for its own sake
// — it's that the node family looks/feels like ONE system instead of
// 8 components that drifted independently. A future palette tweak
// touches one file, not 8.

// Tiny ALL-CAPS pip header used at the top of every content-bearing
// node ("⚡ Synthesis", "🤖 Assistant", "👤 You", "▸ N peers", etc).
// Pair with a per-role color via the caller's classname tail.
export const PIP_HEADER_CLASS =
  'text-[9px] uppercase tracking-widest font-bold';

// Compact mono meta line for token / cost / duration chips. Used in
// the asst marker, the synth round, the peer card, the fan-collapsed
// chip, etc. Caller provides the per-context opacity / color tail.
export const META_LINE_CLASS = 'text-[10px] font-mono';

// Italic in-card lead-in for INPUT-side previews ("asks:", "args:").
// Emerald per the input/output color split (peer asks: + tool args:).
// Caller provides dark/light mode tail (text-emerald-300/80 vs 700/80).
export const INPUT_LEAD_CLASS = 'text-[10px] italic';

// Italic in-card lead-in for OUTPUT-side previews ("says:" on peers,
// "↓ answer" on synth). Cyan per the input/output color split.
export const OUTPUT_LEAD_CLASS = 'text-[10px] italic leading-tight';

// Hover-lift transition shared by every CLICKABLE node card so the
// whole map "responds" the same way to mouse hover. Card-deck nodes
// (FanCollapsedChip, CollapsedPhaseStackNode) bake this into their
// own className; clickable spine nodes opt in via this token.
export const HOVER_LIFT_CLASS =
  'transition-transform hover:-translate-y-0.5 hover:shadow-md';

// Uniform selection ring applied when ReactFlow's `selected` prop is
// true. Shared so click + future keyboard nav land the same visual.
export const SELECTION_RING_CLASS = 'ring-2 ring-emerald-500/70';

// Trailmap L1 (plan Task 15 + spec §8.5) — viewport + visibility budget.
// MAX_VISIBLE_TURNS_PER_TRAIL is the per-trail rendered-turns cap;
// trails with more turns get a "load older" affordance at the top of
// the off-screen portion. REACT_FLOW_ONLY_VISIBLE pins ReactFlow's
// onlyRenderVisibleElements so at 100+ turns × 30 trails (~3000 nodes)
// we don't paint everything on every viewport transform.
export const MAX_VISIBLE_TURNS_PER_TRAIL = 80;
// Living Blueprint SP1 — single source of truth for the summoned blueprint
// flip-card footprint. The card CSS fixes height:500px; width 420 eases the
// 4-cell front grid (was cramped at 360). Imported by layoutGroups (DIM_W/H)
// and the emit site (chatDagDataProcessor) so the footprint never drifts.
export const BLUEPRINT_NODE_SIZE = { w: 420, h: 500 };
// 2026-05-15: flipped to FALSE after operator TS-03/TS-04 testing showed
// the culling drops nodes that land 1-2px outside the post-execution
// fitView's viewport. The original justification ("100 turns × 30 trails
// = 3000 nodes") is real but well past current scale (19-node sessions
// in active testing). For typical chat sizes (<500 nodes), ReactFlow
// renders all nodes cheaply. Reinstate the optimization with a node-
// count threshold guard when actual large-trail load testing happens.
export const REACT_FLOW_ONLY_VISIBLE = false;

// Trailmap L2 — reflow + interaction staging (spec §0.3, §4.4).
// Tile positions tween over REFLOW_TWEEN_MS on dim-switch / expansion;
// INTERACTION_BEAT_MS is the breath between tile tween settle and the
// camera fitBounds call so the eye can read the new layout before the
// camera moves.
export const REFLOW_TWEEN_MS = 400;
export const INTERACTION_BEAT_MS = 50;

// Trailmap L2 — expansion caps (DC-D7, spec §6.1).
// Soft cap shows a "6+" PillBar badge; hard cap triggers LRU eviction.
// Per-type sub-caps prevent any single heavy-renderer family (Mermaid,
// MapLibre, code-highlighter) from monopolizing the WebGL/CPU budget.
export const MAX_EXPANSIONS_SOFT = 6;
export const MAX_EXPANSIONS_TOTAL = 12;
export const MAX_EXPANSIONS_PER_TYPE: Record<string, number> = {
  mermaid: 3,
  diagram: 3,
  mindmap: 3,
  geojson: 2,
  code: 4,
};

// Trailmap L2 Drip 3 — expanded tile footprints by artifact type (spec §4.3,
// adapted from L2 §6.2). Fed into the node's explicit width/height so the
// height-aware clustering path (placeColumns) reflows around it. Mermaid uses
// a fixed box this drip (SVG-natural min/max deferred — lean).
export const EXPANDED_TILE_SIZE: Record<string, { w: number; h: number }> = {
  markdown: { w: 600, h: 640 }, text: { w: 600, h: 640 }, document: { w: 600, h: 640 },
  code: { w: 600, h: 640 },
  mermaid: { w: 640, h: 520 }, diagram: { w: 640, h: 520 }, mindmap: { w: 640, h: 520 },
  geojson: { w: 600, h: 480 }, image: { w: 600, h: 480 },
  json: { w: 500, h: 400 }, yaml: { w: 500, h: 400 }, toml: { w: 500, h: 400 }, csv: { w: 500, h: 400 },
  idea: { w: 600, h: 480 }, critique: { w: 600, h: 480 }, solution: { w: 600, h: 480 }, prompt: { w: 600, h: 480 },
};
export const EXPANDED_TILE_FALLBACK = { w: 480, h: 320 };
export function expandedTileSize(type: string | undefined): { w: number; h: number } {
  return (type && EXPANDED_TILE_SIZE[type]) || EXPANDED_TILE_FALLBACK;
}

// Trailmap L2 — renderer cache ceilings (spec §3.5).
// Cache holds imperative resources (Mermaid SVG strings, MapLibre Map
// instances) — NOT React subtrees. LRU evicts past entry / RAM caps.
// MAX_MAPLIBRE_INSTANCES separately gates concurrent WebGL contexts.
export const MAX_RENDERER_CACHE_ENTRIES = 20;
export const MAX_RENDERER_CACHE_RAM_MB = 8;
export const MAX_MAPLIBRE_INSTANCES = 6;

// Trailmap L2 — UX timing (spec §5.5, §6.1, §7.3).
// ARIA_LIVE_DEBOUNCE_MS coalesces rapid state writes to live regions;
// TOAST_PULSE_BUDGET_MS bounds the auto-nav toast's 1Hz pulse before
// it settles to a static chip; LRU_EVICT_FLIGHT_MS is the wait between
// LRU eviction and the actual unmount; ESC_POST_HOVERCARD_DEBOUNCE_MS
// suppresses ESC mass-collapse for a moment after a HoverCard closes
// so a single ESC press doesn't both close the card AND collapse all
// tiles; LRU_UNDO_WINDOW_MS is the Cmd-Z window after an eviction.
export const ARIA_LIVE_DEBOUNCE_MS = 150;
export const TOAST_PULSE_BUDGET_MS = 5000;
export const LRU_EVICT_FLIGHT_MS = 200;
export const ESC_POST_HOVERCARD_DEBOUNCE_MS = 300;
export const LRU_UNDO_WINDOW_MS = 3000;

// Trailmap L2 — auto-nav timing (spec §7.1).
// REFLOW_FIT_DURATION is the camera tween length after dim switch;
// EXPAND_FIT_DURATION is the camera tween after tile expansion;
// CLUSTER_CLICK_FIT_PADDING is the bounds padding when fitting a
// cluster strip click; TILE_FIT_MAX_ZOOM caps how close the camera
// goes on tile fit so we don't end up with single-pixel-wide tile.
export const REFLOW_FIT_DURATION = 600;
export const REFLOW_FIT_PADDING = 0.22;
export const EXPAND_FIT_DURATION = 600;
export const EXPAND_FIT_PADDING = 0.30;
export const CLUSTER_CLICK_FIT_PADDING = 0.18;
export const TILE_FIT_DURATION = 600;
export const TILE_FIT_PADDING = 0.10;
export const TILE_FIT_MAX_ZOOM = 1.5;
// Trailmap L2 Drip 3 — pan-floor reveal after an expansion. Snappier than the
// 600ms fits (it's a nudge, not a full fit) and pan-only (zoom unchanged).
export const PAN_FLOOR_DURATION = 350;

// Trailmap L2 — layout constants (spec §4.2).
// CLUSTER_COLUMN_GAP is the horizontal pixel gap between cluster
// columns when dim ∈ {phase, persona}; PROGRESS_HEARTBEAT_MS is the
// interval at which a long-running tween emits a polite-live progress
// signal so the user knows the canvas hasn't frozen.
export const CLUSTER_COLUMN_GAP = 64;
export const PROGRESS_HEARTBEAT_MS = 10000;

// SP-3 — stance encoding tokens. Stance uses a DIFFERENT visual channel
// than status (which owns color via statusPalette.ts). Stance uses glyph +
// border-style. Text glyphs only (no emoji — they don't scale below 11px).
// Border-style is not color-scheme-dependent so no dark: variants needed.
export interface StanceToken {
  glyph: string;
  label: string;
  borderClass: string;
}

export const STANCE_TOKENS: Record<string, StanceToken> = {
  neutral: {
    glyph: '○',
    label: 'neutral',
    borderClass: 'border-slate-300 dark:border-slate-500',
  },
  skeptical: {
    glyph: '⊘',
    label: 'skeptical',
    borderClass: 'border-violet-400 border-dashed dark:border-violet-400',
  },
  allegiant: {
    glyph: '⊕',
    label: 'allegiant',
    borderClass: 'border-sky-400 border-double border-[3px] dark:border-sky-400',
  },
};

export function getStanceToken(stance: string | undefined): StanceToken {
  return STANCE_TOKENS[stance ?? 'neutral'] ?? STANCE_TOKENS.neutral;
}

// Source Spines SP-2 — per-bubble entry-cascade stagger. Bubble N animates in
// at index*STAGGER ms after its lane expands (staggered reveal, not a pop).
export const SPINE_BUBBLE_STAGGER_MS = 60;
